/**
 * Redux Store Configuration
 * 
 * This module configures the Redux store with tenant-specific API middleware.
 */

import { configureStore } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import storage from 'redux-persist/lib/storage';
import { combineReducers } from 'redux';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE
} from 'redux-persist';

// Import auth and download slices
import auth from 'src/core/features/auth/authSlice';
import downloadFileReducer from 'src/core/features/download/downloadFileSlice';

// Import the active tenant API
import { activeTenantApi, activeTenantConfig } from './drheranca';

// Import core auth API and config initialization
import { getAuthApi } from 'src/core/auth/api/authApi';
import { initializeGlobalConfig } from 'src/core/config';

// Initialize global config before creating store
// This allows the auth API to read the config during store setup
initializeGlobalConfig(activeTenantConfig);

// Get or create auth API (reads from global config)
const authApi = getAuthApi();

// Build root reducer with app API and auth API
const rootReducer = combineReducers({
  [activeTenantApi.reducerPath]: activeTenantApi.reducer,
  [authApi.reducerPath]: authApi.reducer,
  auth,
  dashReducer: downloadFileReducer
});

const persistConfig = {
  key: 'root',
  version: 1,
  storage,
  whitelist: ['auth']
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) => {
    const middleware = getDefaultMiddleware({
      thunk: {
        extraArgument: downloadFileReducer
      },
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
      }
    });
    
    const apiMiddleware = [];
    if (activeTenantApi.middleware) {
      apiMiddleware.push(activeTenantApi.middleware);
    }
    if (authApi.middleware) {
      apiMiddleware.push(authApi.middleware);
    }
    
    return middleware.concat(apiMiddleware);
  },
});

export const persistor = persistStore(store);

// Define types after store creation to avoid circular references
export type AppDispatch = typeof store.dispatch;
export const useAppDispatch = () => useDispatch<AppDispatch>();

// Use a helper type to avoid circular reference issues
type StoreState = typeof store extends { getState: () => infer S } ? S : never;
export type RootState = StoreState;
export const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector;
